import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import VolcanMapa from "../../components/VolcanMapa";
import { getVolcanes } from "../services/volcanService";
import { getHomeNoticias } from "../services/siteContentService";
import { getImagenPreviewUrl, getMediaUrl, hasImagen } from "../../utils/api";
import {
  EXTERNAL_VIDEO_IFRAME_PROPS,
  toEmbedUrl,
} from "../../utils/videoEmbed";
import "./HomePage.css";
import heroBanner from "../../assets/images/home/banner_historico_volcanes.jpg";
import homeVideoPoster from "../../assets/images/home/visor_1_poster.jpg";
import homeVideoPlayIcon from "../../assets/images/home/icon-play-sgc.png";

const HERO_BANNER_SRC = heroBanner;
const HOME_VIDEO_SRC = `${process.env.PUBLIC_URL}/videos/visor_1.mp4`;
const HOME_VIDEO_POSTER_SRC = homeVideoPoster;
const HOME_VIDEO_PLAY_ICON_SRC = homeVideoPlayIcon;
const HOME_STORIES_PLAY_ICON_SRC = HOME_VIDEO_PLAY_ICON_SRC;

function youtubeThumbnail(youtubeId) {
  const id = String(youtubeId ?? "").trim();
  if (!id) return "";
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

function getNoticiaEmbedSrc(noticia) {
  const embed = String(noticia?.embed_url ?? "").trim();
  const watch = String(noticia?.url_youtube ?? "").trim();
  const fromId = noticia?.youtube_id
    ? toEmbedUrl(`https://www.youtube.com/watch?v=${noticia.youtube_id}`)
    : "";
  return toEmbedUrl(embed || watch || fromId);
}

function getNoticiaWatchUrl(noticia) {
  const watch = String(noticia?.url_youtube ?? "").trim();
  if (watch) return watch;
  const id = String(noticia?.youtube_id ?? "").trim();
  return id ? `https://www.youtube.com/watch?v=${id}` : "";
}

function embedUrlWithAutoplay(embedSrc) {
  if (!embedSrc) return "";
  try {
    const url = new URL(embedSrc);
    url.searchParams.set("autoplay", "1");
    url.searchParams.set("rel", "0");
    return url.toString();
  } catch {
    return embedSrc;
  }
}

function getNoticiaImage(noticia) {
  const imagen = noticia?.imagen;
  if (!imagen) {
    const fallback = youtubeThumbnail(noticia?.youtube_id);
    if (!fallback) return null;
    return { src: fallback, width: 337, height: 600 };
  }

  const original = imagen.original ? getMediaUrl(imagen.original) : "";
  const preview = imagen.vista_previa ? getMediaUrl(imagen.vista_previa) : "";
  const src = original || preview;
  if (!src) return null;

  const width =
    Number(imagen.ancho_original || imagen.ancho_vista_previa) || 337;
  const height =
    Number(imagen.alto_original || imagen.alto_vista_previa) || 600;
  const previewWidth = Number(imagen.ancho_vista_previa) || 337;

  const srcSet =
    original && preview
      ? `${preview} ${previewWidth}w, ${original} ${width}w`
      : undefined;

  return {
    src: preview || original,
    srcSet,
    sizes: "(max-width: 640px) 184px, (max-width: 900px) 198px, 232px",
    width,
    height,
  };
}

function HomeStoryCard({ noticia, onOpen }) {
  const image = getNoticiaImage(noticia);
  const titulo = String(noticia.titulo ?? "").trim();

  return (
    <button
      type="button"
      className="home-stories-card"
      onClick={() => onOpen(noticia)}
      aria-label={titulo ? `Reproducir video: ${titulo}` : "Reproducir video"}
    >
      <div className="home-stories-card__media">
        {image ? (
          <img
            className="home-stories-card__thumb"
            src={image.src}
            srcSet={image.srcSet}
            sizes={image.sizes}
            alt=""
            width={image.width}
            height={image.height}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="home-stories-card__thumb home-stories-card__thumb--empty" />
        )}
        <img
          className="home-stories-card__play"
          src={HOME_STORIES_PLAY_ICON_SRC}
          alt=""
          aria-hidden="true"
        />
      </div>
      {titulo ? (
        <span className="home-stories-card__caption">{titulo}</span>
      ) : null}
    </button>
  );
}

function HomeStoriesModal({ noticia, onClose }) {
  const embedSrc = embedUrlWithAutoplay(getNoticiaEmbedSrc(noticia));
  const watchUrl = getNoticiaWatchUrl(noticia);
  const titulo = String(noticia?.titulo ?? "").trim();

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [onClose]);

  if (!embedSrc) return null;

  return (
    <div className="home-stories-modal" role="presentation" onClick={onClose}>
      <div
        className="home-stories-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={titulo || "Video de YouTube"}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="home-stories-modal__player">
          <button
            type="button"
            className="home-stories-modal__close"
            onClick={onClose}
            aria-label="Cerrar video"
          >
            ×
          </button>
          <iframe
            {...EXTERNAL_VIDEO_IFRAME_PROPS}
            className="home-stories-modal__iframe"
            src={embedSrc}
            title={titulo || "Video de YouTube"}
          />
        </div>

        {watchUrl ? (
          <a
            href={watchUrl}
            className="home-stories-modal__youtube"
            target="_blank"
            rel="noopener noreferrer"
          >
            Mirar en YouTube
            <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
          </a>
        ) : null}
      </div>
    </div>
  );
}

function HomeStoriesSection({ data, loading }) {
  const [activeNoticia, setActiveNoticia] = useState(null);

  const noticias = useMemo(() => {
    const items = Array.isArray(data?.noticias) ? data.noticias : [];
    return [...items].sort(
      (a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0),
    );
  }, [data]);

  if (!loading && noticias.length === 0) return null;

  return (
    <section className="home-stories" aria-labelledby="home-stories-heading">
      <h2 id="home-stories-heading" className="home-stories__title">
        Encuentra algunos videos relacionados con la actividad volcánica de
        Colombia
      </h2>

      {loading ? (
        <p className="home-stories__status">Cargando historias…</p>
      ) : (
        <div className="home-stories__grid">
          {noticias.map((noticia) => (
            <HomeStoryCard
              key={noticia.id}
              noticia={noticia}
              onOpen={setActiveNoticia}
            />
          ))}
        </div>
      )}

      {activeNoticia ? (
        <HomeStoriesModal
          noticia={activeNoticia}
          onClose={() => setActiveNoticia(null)}
        />
      ) : null}
    </section>
  );
}

function HomeVideoPlayer() {
  const videoRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  const handlePlay = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      await video.play();
      setHasStarted(true);
    } catch {
      // El navegador puede bloquear la reproducción automática.
    }
  };

  const handleEnded = () => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    setHasStarted(false);
  };

  return (
    <section className="home-video" aria-labelledby="home-video-heading">
      <h2 id="home-video-heading" className="home-video__title">
        Así funciona nuestro sistema de información
      </h2>
      <div className="home-video__frame">
        <video
          ref={videoRef}
          className={`home-video__player${hasStarted ? " is-playing" : ""}`}
          src={HOME_VIDEO_SRC}
          poster={HOME_VIDEO_POSTER_SRC}
          width={2152}
          height={1080}
          controls={hasStarted}
          playsInline
          preload="none"
          onPlay={() => setHasStarted(true)}
          onEnded={handleEnded}
        >
          Tu navegador no soporta la reproducción de video.
        </video>

        {!hasStarted ? (
          <>
            <img
              className="home-video__poster"
              src={HOME_VIDEO_POSTER_SRC}
              alt=""
              aria-hidden="true"
            />
            <button
              type="button"
              className="home-video__overlay"
              onClick={handlePlay}
              aria-label="Reproducir video del visor"
            >
              <img
                className="home-video__play-icon"
                src={HOME_VIDEO_PLAY_ICON_SRC}
                alt=""
                aria-hidden="true"
              />
            </button>
          </>
        ) : null}
      </div>
    </section>
  );
}

function stripHtml(value) {
  if (!value) return "";
  const doc = new DOMParser().parseFromString(String(value), "text/html");
  return doc.body.textContent?.replace(/\s+/g, " ").trim() ?? "";
}

function ubicacionLabel(volcan) {
  const text =
    volcan?.ubicacion ||
    volcan?.departamento ||
    volcan?.region ||
    volcan?.departamentos;
  if (text) return String(text).trim();

  const loc = volcan?.localizacion;
  if (!loc) return null;
  const s = String(loc).trim();
  if (/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(s)) return null;
  return s;
}

function elevacionLabel(volcan) {
  const raw = volcan?.elevacion;
  if (!raw) return null;
  const s = String(raw).trim();
  if (/m\.?\s*s\.?\s*n\.?\s*m|msnm|msmn/i.test(s)) return `Altura: ${s}`;
  return `Altura: ${s} m s. n. m.`;
}

function HomeVolcanCard({ volcan, selected, onHighlight, onClear }) {
  const imageUrl = hasImagen(volcan.imagen_detalle)
    ? getImagenPreviewUrl(volcan.imagen_detalle)
    : null;
  const descripcion =
    stripHtml(volcan.descripcion_corta) ||
    stripHtml(volcan.resumen) ||
    stripHtml(volcan.descripcion);
  const ubicacion = ubicacionLabel(volcan);
  const altura = elevacionLabel(volcan);

  return (
    <Link
      to={`/volcan-detalle?id=${volcan.id}`}
      className={`home-volcan-card${selected ? " home-volcan-card--active" : ""}`}
      aria-label={`Ver información de ${volcan.title}`}
      onMouseEnter={() => onHighlight(volcan)}
      onFocus={() => onHighlight(volcan)}
      onBlur={(event) => {
        const related = event.relatedTarget;
        if (related instanceof Node) {
          if (related.closest?.(".home-volcan-card")) return;
          if (related.closest?.(".home-volcanes__map-wrap")) return;
        }
        onClear?.();
      }}
    >
      {imageUrl ? (
        <img
          className="home-volcan-card__bg"
          src={imageUrl}
          alt=""
          loading="lazy"
        />
      ) : (
        <div className="home-volcan-card__bg home-volcan-card__bg--empty" />
      )}

      <div className="home-volcan-card__front">
        <h3 className="home-volcan-card__title">{volcan.title}</h3>
      </div>

      <div className="home-volcan-card__back">
        <h3 className="home-volcan-card__title home-volcan-card__title--back">
          {volcan.title}
        </h3>
        <p className="home-volcan-card__desc">{descripcion || ""}</p>
        <div className="home-volcan-card__meta">
          {ubicacion ? (
            <span className="home-volcan-card__meta-item">
              <i className="bi bi-geo-alt-fill" aria-hidden />
              {ubicacion}
            </span>
          ) : (
            <span className="home-volcan-card__meta-item" aria-hidden="true" />
          )}
          {altura ? (
            <span className="home-volcan-card__meta-item home-volcan-card__meta-item--right">
              {altura}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const [volcanes, setVolcanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVolcan, setSelectedVolcan] = useState(null);
  const [storiesData, setStoriesData] = useState(null);
  const [storiesLoading, setStoriesLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const data = await getVolcanes();
        setVolcanes(Array.isArray(data) ? data : []);
      } catch {
        setError("No se pudo cargar el listado de volcanes");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchStories() {
      try {
        setStoriesLoading(true);
        const data = await getHomeNoticias();
        setStoriesData(data);
      } catch {
        setStoriesData({ introduccion: "", noticias: [] });
      } finally {
        setStoriesLoading(false);
      }
    }
    fetchStories();
  }, []);

  const volcanesOrdenados = useMemo(
    () =>
      [...volcanes].sort((a, b) =>
        String(a?.title ?? "").localeCompare(String(b?.title ?? ""), "es"),
      ),
    [volcanes],
  );

  const volcanesColumnas = useMemo(() => {
    const mitad = Math.ceil(volcanesOrdenados.length / 2);
    return {
      izquierda: volcanesOrdenados.slice(0, mitad),
      derecha: volcanesOrdenados.slice(mitad),
    };
  }, [volcanesOrdenados]);

  const handleHighlightVolcan = useCallback((volcan) => {
    setSelectedVolcan(volcan);
  }, []);

  const handleClearHighlight = useCallback(() => {
    setSelectedVolcan(null);
  }, []);

  const handleVolcanBlur = useCallback((volcanId) => {
    setSelectedVolcan((current) =>
      current && String(current.id) === String(volcanId) ? null : current,
    );
  }, []);

  const handleSidebarMouseLeave = useCallback(
    (event) => {
      const related = event.relatedTarget;
      if (related instanceof Node && related.closest?.(".home-volcan-card")) {
        return;
      }
      handleClearHighlight();
    },
    [handleClearHighlight],
  );

  const handleMapMouseLeave = useCallback(
    (event) => {
      const related = event.relatedTarget;
      if (related instanceof Node) {
        if (related.closest?.(".home-volcan-card")) return;
        if (related.closest?.(".leaflet-popup")) return;
        if (related.closest?.(".leaflet-marker-icon")) return;
      }
      handleClearHighlight();
    },
    [handleClearHighlight],
  );

  const goDetalle = useCallback(
    (id) => {
      navigate(`/volcan-detalle?id=${id}`);
    },
    [navigate],
  );

  return (
    <div className="home-page">
      <section className="home-hero" aria-label="Banner principal">
        <img
          className="home-hero__image"
          src={HERO_BANNER_SRC}
          alt="Conoce la actividad histórica de los volcanes colombianos"
          width={1880}
          height={552}
        />
      </section>

      <section className="home-intro" aria-label="Introducción">
        <p className="home-intro__text">
          <strong>Este sistema</strong> reúne registros históricos sobre los
          volcanes colombianos{" "}
          <strong>desde el siglo XVI hasta la actualidad</strong>, elaborados
          por <strong>viajeros, cronistas, naturalistas y científicos</strong>,
          entre otros, quienes los han observado desde diversas perspectivas.
          Tras un riguroso análisis técnico y científico se pone a disposición
          una amplia variedad de información sistematizada que incluye{" "}
          <strong>mapas, fotografías y documentos</strong>, entre otros.
        </p>
        <p className="home-intro__text">
          De esta manera se facilita el análisis del tema, se promueve la
          investigación y se garantiza el{" "}
          <strong>
            acceso tanto a sectores académicos e institucionales como al público
            en general
          </strong>
          , contribuyendo al fortalecimiento de la{" "}
          <strong>
            gestión del riesgo y a una mejor comprensión de las erupciones
            pasadas.
          </strong>
        </p>
      </section>

      <section
        className="home-volcanes"
        aria-labelledby="home-volcanes-heading"
      >
        <div className="home-volcanes__explorer">
          <h2 id="home-volcanes-heading" className="home-volcanes__heading">
            Navega para conocer sobre la historia de
            <br className="home-volcanes__heading-br" /> nuestros volcanes
          </h2>

          {loading ? (
            <p className="home-volcanes__status">Cargando volcanes…</p>
          ) : null}
          {error ? (
            <p className="home-volcanes__status home-volcanes__status--error">
              {error}
            </p>
          ) : null}

          {!loading && !error ? (
            <div
              className="home-volcanes__layout"
              onClick={(event) => {
                if (event.target.closest(".home-volcanes__map-wrap")) return;
                if (event.target.closest(".home-volcan-card")) return;
                handleClearHighlight();
              }}
            >
              <aside
                className="home-volcanes__sidebar home-volcanes__sidebar--left"
                aria-label="Volcanes (columna izquierda)"
                onMouseLeave={handleSidebarMouseLeave}
              >
                <div className="home-volcanes__list">
                  {volcanesColumnas.izquierda.map((volcan) => (
                    <HomeVolcanCard
                      key={volcan.id}
                      volcan={volcan}
                      selected={selectedVolcan?.id === volcan.id}
                      onHighlight={handleHighlightVolcan}
                      onClear={handleClearHighlight}
                    />
                  ))}
                </div>
              </aside>

              <div
                className="home-volcanes__map-wrap"
                onMouseLeave={handleMapMouseLeave}
              >
                <VolcanMapa
                  volcanes={volcanesOrdenados}
                  selectedVolcanId={selectedVolcan?.id ?? null}
                  onMoreInfo={goDetalle}
                  onVolcanFocus={handleHighlightVolcan}
                  onVolcanBlur={handleVolcanBlur}
                  onClearSelection={handleClearHighlight}
                />
              </div>

              <aside
                className="home-volcanes__sidebar home-volcanes__sidebar--right"
                aria-label="Volcanes (columna derecha)"
                onMouseLeave={handleSidebarMouseLeave}
              >
                <div className="home-volcanes__list">
                  {volcanesColumnas.derecha.map((volcan) => (
                    <HomeVolcanCard
                      key={volcan.id}
                      volcan={volcan}
                      selected={selectedVolcan?.id === volcan.id}
                      onHighlight={handleHighlightVolcan}
                      onClear={handleClearHighlight}
                    />
                  ))}
                </div>
              </aside>
            </div>
          ) : null}

          <p className="home-volcanes__footnote home-intro__text">
            Actualmente se conocen 25 estructuras volcánicas activas y
            monitoreadas en el país y seguimos trabajando para que pronto
            conozcas más sobre ellas.
          </p>
        </div>
      </section>

      <HomeVideoPlayer />

      <HomeStoriesSection data={storiesData} loading={storiesLoading} />
    </div>
  );
}

export default HomePage;
